/**
 * "My links" (#2089 SIDECFG). Server-backed: D1 `user_sidebar_entries` via
 * /api/preferences/sidebar-entries (owner-scoped). The shape mirrors
 * lib/gadget-pins.ts: one query the whole shell shares, mutations that update
 * the cached list in place and roll back on failure, and `unavailable` true
 * until the list has loaded on a host that has applied the migration.
 */
import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiMutate } from "@/lib/api";
import type { SidebarEntry, SidebarEntryKind } from "@shared/sidebar-entries";

export interface SidebarEntriesResponse { entries: SidebarEntry[]; pending_migration?: boolean }

export const SIDEBAR_ENTRIES_KEY = ["sidebar-entries"] as const;
const BASE = "/api/preferences/sidebar-entries";

export function useSidebarEntriesQuery(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: SIDEBAR_ENTRIES_KEY,
    queryFn: () => apiFetch<SidebarEntriesResponse>(BASE),
    staleTime: 60 * 1000,
    enabled: opts.enabled ?? true,
  });
}

export interface AddSidebarEntry { kind: SidebarEntryKind; target: string; label?: string }

export interface SidebarEntriesApi {
  entries: SidebarEntry[];
  /** True until the list has loaded, or while the host has not applied migrate-2089-user-sidebar-entries.sql. */
  unavailable: boolean;
  /** The entry for a (kind, target) pair, if saved. */
  find: (kind: SidebarEntryKind, target: string) => SidebarEntry | undefined;
  add: (input: AddSidebarEntry) => Promise<SidebarEntry>;
  rename: (id: number, label: string) => Promise<void>;
  remove: (id: number) => Promise<void>;
  move: (id: number, direction: "up" | "down") => Promise<void>;
  /** The last add's failure text (cap reached, bad link), cleared by the next add. */
  addError: string | null;
}

export function useSidebarEntries(opts: { enabled?: boolean } = {}): SidebarEntriesApi {
  const qc = useQueryClient();
  const q = useSidebarEntriesQuery(opts);
  const entries = useMemo(() => q.data?.entries ?? [], [q.data]);
  const setList = useCallback(
    (fn: (cur: SidebarEntry[]) => SidebarEntry[]) =>
      qc.setQueryData<SidebarEntriesResponse>(SIDEBAR_ENTRIES_KEY, (cur) => ({ ...(cur ?? {}), entries: fn(cur?.entries ?? []) })),
    [qc],
  );
  const invalidate = useCallback(() => qc.invalidateQueries({ queryKey: SIDEBAR_ENTRIES_KEY }), [qc]);

  const addMutation = useMutation({
    mutationFn: (input: AddSidebarEntry) => apiMutate<{ entry: SidebarEntry }>(BASE, "POST", input),
    onSuccess: ({ entry }) => setList((list) => (list.some((e) => e.id === entry.id) ? list : [...list, entry])),
    onSettled: invalidate,
  });
  const renameMutation = useMutation({
    mutationFn: ({ id, label }: { id: number; label: string }) => apiMutate<{ entry: SidebarEntry }>(`${BASE}/${id}`, "PATCH", { label }),
    onMutate: async ({ id, label }) => {
      await qc.cancelQueries({ queryKey: SIDEBAR_ENTRIES_KEY });
      const prev = qc.getQueryData<SidebarEntriesResponse>(SIDEBAR_ENTRIES_KEY);
      setList((list) => list.map((e) => (e.id === id ? { ...e, label } : e)));
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(SIDEBAR_ENTRIES_KEY, ctx.prev); },
    onSettled: invalidate,
  });
  const removeMutation = useMutation({
    mutationFn: (id: number) => apiMutate<{ ok: boolean }>(`${BASE}/${id}`, "DELETE"),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: SIDEBAR_ENTRIES_KEY });
      const prev = qc.getQueryData<SidebarEntriesResponse>(SIDEBAR_ENTRIES_KEY);
      setList((list) => list.filter((e) => e.id !== id));
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(SIDEBAR_ENTRIES_KEY, ctx.prev); },
    onSettled: invalidate,
  });
  const moveMutation = useMutation({
    mutationFn: ({ id, direction }: { id: number; direction: "up" | "down" }) =>
      apiMutate<{ entries: SidebarEntry[] }>(`${BASE}/${id}/move`, "POST", { direction }),
    onMutate: async ({ id, direction }) => {
      await qc.cancelQueries({ queryKey: SIDEBAR_ENTRIES_KEY });
      const prev = qc.getQueryData<SidebarEntriesResponse>(SIDEBAR_ENTRIES_KEY);
      setList((list) => moveInList(list, id, direction));
      return { prev };
    },
    onSuccess: ({ entries: fresh }) => setList(() => fresh),
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(SIDEBAR_ENTRIES_KEY, ctx.prev); },
    onSettled: invalidate,
  });

  const { mutateAsync: addAsync, error: addErr } = addMutation;
  const { mutateAsync: renameAsync } = renameMutation;
  const { mutateAsync: removeAsync } = removeMutation;
  const { mutateAsync: moveAsync } = moveMutation;

  return {
    entries,
    unavailable: !q.isSuccess || q.data?.pending_migration === true,
    find: (kind, target) => entries.find((e) => e.kind === kind && e.target === target),
    add: useCallback(async (input: AddSidebarEntry) => (await addAsync(input)).entry, [addAsync]),
    rename: useCallback(async (id: number, label: string) => { await renameAsync({ id, label }); }, [renameAsync]),
    remove: useCallback(async (id: number) => { await removeAsync(id); }, [removeAsync]),
    move: useCallback(async (id: number, direction: "up" | "down") => { await moveAsync({ id, direction }); }, [moveAsync]),
    addError: addErr ? (addErr as Error).message : null,
  };
}

/** The list with `id` swapped one step in `direction`; unchanged at the edge. */
export function moveInList(list: SidebarEntry[], id: number, direction: "up" | "down"): SidebarEntry[] {
  const idx = list.findIndex((e) => e.id === id);
  const other = direction === "up" ? idx - 1 : idx + 1;
  if (idx === -1 || other < 0 || other >= list.length) return list;
  const next = list.slice();
  [next[idx], next[other]] = [next[other], next[idx]];
  return next;
}
